// ══════════════════════════════════════════════════════════════════════════
// FILE 3 of 4: Cards.jsx
// Two 756×945 result cards — DiagnosticsCard and RoutineCard — sharing a
// header, top section (photo + stat block), and footer. These render
// off-screen and get captured with html-to-image; see ShareCards.jsx.
// ══════════════════════════════════════════════════════════════════════════
import { forwardRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  FiFeather,
  FiSmile,
  FiTarget,
  FiDroplet,
  FiSun,
  FiClipboard,
  FiCheckCircle,
  FiBarChart2,
} from 'react-icons/fi'
import './Style.css'
import { Header, DnaCol, TopSection, CardFooter } from './CardParts'

const HAIR_TYPE_LABELS = {
  '1A': 'STRAIGHT',
  '1B': 'STRAIGHT',
  '1C': 'STRAIGHT',
  '2A': 'WAVY',
  '2B': 'WAVY',
  '2C': 'WAVY',
  '3A': 'CURLY',
  '3B': 'CURLY',
  '3C': 'CURLY',
  '4A': 'COILY',
  '4B': 'COILY',
  '4C': 'COILY',
}

const normalizeAnswerValue = (value) => {
  if (value == null) return null
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value))
    return value.filter(Boolean).join(', ').trim() || null
  if (typeof value === 'object') {
    return Object.values(value)
      .filter((item) => item != null && item !== '')
      .join(', ')
      .trim()
  }
  return String(value).trim()
}

const getAnswersMap = (answersList = []) =>
  answersList.reduce((acc, item) => {
    if (!item) return acc
    const key = item.saveKey || item.stepId
    if (!key) return acc
    acc[key] =
      item.extractedValue ?? item.value ?? item.text ?? item.photoUrl ?? null
    return acc
  }, {})

const getFinalHairClass = (answersMap) => {
  const predicted = normalizeAnswerValue(
    answersMap?.hairPrediction?.predictedHairClass ||
      answersMap?.predictedHairClass,
  )
  const corrected = normalizeAnswerValue(answersMap?.correctedHairClass)
  const isPredictionCorrect = normalizeAnswerValue(
    answersMap?.isPredictedHairClassCorrect,
  )
  if (String(isPredictionCorrect).toLowerCase() === 'no' && corrected) {
    return corrected
  }
  return predicted || corrected || null
}

const getHairTypeName = (hairClass) =>
  HAIR_TYPE_LABELS[String(hairClass).trim()] || null

const getChatHairProfile = (chatMessages) => {
  const hairProfileMessage = chatMessages.find(
    (m) => m.from === 'ai' && m.hairProfile,
  )
  return hairProfileMessage?.hairProfile || null
}

const getChatPhotoUrl = (answersList, chatMessages, preview) => {
  const chatHairProfile = getChatHairProfile(chatMessages)
  if (chatHairProfile?.hairPhoto) return chatHairProfile.hairPhoto

  const hairPhotoMessage = chatMessages.find(
    (m) => m.from === 'ai' && (m.photoUrl || m.image),
  )
  if (hairPhotoMessage)
    return hairPhotoMessage.photoUrl || hairPhotoMessage.image

  const photoAnswer = answersList.find(
    (a) => a.stepId === 'hair_photo' || a.saveKey === 'hair_photo',
  )
  if (photoAnswer?.photoUrl) return photoAnswer.photoUrl
  if (photoAnswer?.image) return photoAnswer.image

  return preview || null
}

const getHairGoal = (answersMap, agentData) =>
  normalizeAnswerValue(
    answersMap?.hairProfile?.hair_goals || answersMap?.hair_goals,
  ) ||
  normalizeAnswerValue(agentData?.hair_goals) ||
  null

// NOTE: "Hair Mood" and "Scalp Story" aren't asked directly in the chat flow —
// these derive from the closest existing answers. Wire up dedicated questions
// later if you want more precise copy here.
const getHairMood = (answersMap) => {
  const state = normalizeAnswerValue(
    answersMap?.hairProfile?.hair_state || answersMap?.hair_state,
  )
  const challenges = normalizeAnswerValue(
    answersMap?.hairProfile?.hair_challenges || answersMap?.hair_challenges,
  )
  if (challenges && challenges.toLowerCase() !== 'none')
    return {
      title: challenges,
      desc: 'Something we can help you work through.',
    }
  if (state)
    return { title: state, desc: 'This is where your hair is at right now.' }
  return { title: 'Feeling good', desc: 'No major concerns flagged today.' }
}

const getScalpStory = (answersMap) => {
  const scalp = normalizeAnswerValue(
    answersMap?.hairProfile?.scalp_challenges || answersMap?.scalp_challenges,
  )
  if (scalp && scalp.toLowerCase() !== 'none')
    return { title: scalp, desc: 'Worth keeping an eye on.' }
  return { title: 'Calm & balanced', desc: 'No current scalp concerns.' }
}

const getProductMatch = (productRecommendations, selectedProduct) => {
  if (selectedProduct) return selectedProduct
  return productRecommendations?.price_filtered?.[0] || null
}

// Product objects can come from different places (chat recommendations API,
// selectedProduct in Redux) and may use different keys for the photo.
// Check the common ones instead of assuming a single field name.
const getProductImageUrl = (product) => {
  if (!product) return null
  return (
    product.image ||
    product.image_url ||
    product.imageUrl ||
    product.photo ||
    product.photoUrl ||
    product.thumbnail ||
    product.product_image ||
    product.productImage ||
    (Array.isArray(product.images) ? product.images[0] : null) ||
    null
  )
}

// Shared data both cards need — hair type, photo, tagline, and the fields
// that feed each card's own middle section.
function useResultData(data) {
  const chatCollectedAnswers =
    useSelector((state) => state.chatCollectedAnswers) || []
  const chatMessages = useSelector((state) => state.chatMessages) || []
  const chatPhotoPreview =
    useSelector((state) => state.chatPhotoPreview) || null
  const agentData = useSelector((state) => state.agentData) || {}
  const productRecommendations =
    useSelector((state) => state.productRecommendations) || null
  const selectedProduct = useSelector((state) => state.chatSelectedProduct)

  const answersMap = useMemo(
    () => getAnswersMap(chatCollectedAnswers),
    [chatCollectedAnswers],
  )
  const finalHairClass = useMemo(
    () => getFinalHairClass(answersMap),
    [answersMap],
  )

  const hairTypeCode =
    data.hairTypeCode || finalHairClass || SAMPLE_SHARED.hairTypeCode
  const hairTypeName =
    data.hairTypeName ||
    getHairTypeName(hairTypeCode) ||
    SAMPLE_SHARED.hairTypeName
  const photoUrl =
    data.photoUrl ||
    getChatPhotoUrl(chatCollectedAnswers, chatMessages, chatPhotoPreview)
  const hairGoal =
    data.hairGoal ||
    getHairGoal(answersMap, agentData) ||
    SAMPLE_SHARED.hairGoal
  const hairMood = data.hairMood || getHairMood(answersMap)
  const scalpStory = data.scalpStory || getScalpStory(answersMap)
  const tagline = data.tagline || SAMPLE_SHARED.tagline
  const product =
    data.product ||
    getProductMatch(productRecommendations, selectedProduct) ||
    SAMPLE_SHARED.product
  const productImageUrl = data.product?.image
    ? data.product.image
    : getProductImageUrl(product)

  return {
    hairTypeCode,
    hairTypeName,
    photoUrl,
    hairGoal,
    hairMood,
    scalpStory,
    tagline,
    product,
    productImageUrl,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// DIAGNOSTICS CARD — blue "1": Hair DNA row + Hair Insight panel
// ──────────────────────────────────────────────────────────────────────────
function DiagnosticsCardImpl({ data }, ref) {
  const shared = useResultData(data)
  const insightTitle = data.insightTitle || SAMPLE_DIAGNOSTICS.insightTitle
  const insightDesc = data.insightDesc || SAMPLE_DIAGNOSTICS.insightDesc
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  return (
    <div className='hs-scope' ref={ref}>
      <div className='hs-card'>
        <Header />
        <TopSection {...shared} />

        <div
          className='hs-panel'
          style={{ marginTop: 16, padding: '16px 17px' }}
        >
          <div className='hs-panel-eyebrow'>
            <span className='hs-sparkle'>✦</span>{' '}
            {getTranslationText('shareableFeature.cards.hairDna')}{' '}
            <span className='hs-sparkle'>✦</span>
          </div>
          <div className='hs-dna-row'>
            <DnaCol
              icon={<FiSmile size={16} />}
              label={getTranslationText('shareableFeature.cards.hairMood')}
              title={shared.hairMood.title}
              desc={shared.hairMood.desc}
            />
            <DnaCol
              icon={<FiTarget size={16} />}
              label={getTranslationText('shareableFeature.cards.hairAmbition')}
              title={shared.hairGoal}
              desc={getTranslationText(
                'shareableFeature.cards.hairAmbitionDesc',
              )}
            />
            <DnaCol
              icon={<FiFeather size={16} />}
              label={getTranslationText('shareableFeature.cards.scalpStory')}
              title={shared.scalpStory.title}
              desc={shared.scalpStory.desc}
            />
          </div>
        </div>

        <div
          className='hs-panel hs-info-card'
          style={{ marginTop: 16, flex: 1 }}
        >
          <div
            className='hs-panel-eyebrow'
            style={{ justifyContent: 'center' }}
          >
            💡 {getTranslationText('shareableFeature.cards.hairInsight')}
          </div>
          <div className='hs-insight-title' style={{ textAlign: 'center' }}>
            {insightTitle}
          </div>
          <div className='hs-insight-desc' style={{ textAlign: 'center' }}>
            {insightDesc}
          </div>
        </div>

        <CardFooter />
      </div>
    </div>
  )
}
export const DiagnosticsCard = forwardRef(DiagnosticsCardImpl)

// ──────────────────────────────────────────────────────────────────────────
// ROUTINE CARD — green "2": Product Match panel + Routine steps + Eco Tip
// ──────────────────────────────────────────────────────────────────────────
function RoutineCardImpl({ data }, ref) {
  const shared = useResultData(data)
  const ecoTip = data.ecoTip || SAMPLE_ROUTINE.ecoTip
  const steps = data.steps || SAMPLE_ROUTINE.steps
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  return (
    <div className='hs-scope' ref={ref}>
      <div className='hs-card'>
        <Header />
        <TopSection {...shared} />

        <div className='hs-panel hs-info-card' style={{ marginTop: 16 }}>
          <div
            className='hs-panel-eyebrow'
            style={{ justifyContent: 'flex-start' }}
          >
            <FiCheckCircle size={12} />{' '}
            {getTranslationText('shareableFeature.cards.productMatch')}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {shared.productImageUrl ? (
              <img
                className='hs-product-img'
                src={shared.productImageUrl}
                alt={
                  shared.product?.name ||
                  shared.product?.product_name ||
                  'Product'
                }
              />
            ) : (
              <div className='hs-product-img hs-product-img-placeholder'>
                <FiDroplet size={22} />
              </div>
            )}
            <div>
              <div className='hs-product-name'>
                {shared.product?.name || shared.product?.product_name}
              </div>
              <div className='hs-product-brand'>
                {getTranslationText('shareableFeature.cards.byBrand')}{' '}
                {shared.product?.brand}
              </div>
              <div className='hs-product-desc'>
                {shared.product?.description}
              </div>
              {shared.product?.ingredients && (
                <div className='hs-key-ingredient'>
                  {getTranslationText('shareableFeature.cards.keyIngredient')}{' '}
                  <span>
                    {Array.isArray(shared.product.ingredients)
                      ? shared.product.ingredients[0]
                      : shared.product.ingredients}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className='hs-panel hs-routine-panel-new'
          style={{ marginTop: 16, flex: 1 }}
        >
          <div
            className='hs-panel-eyebrow'
            style={{ justifyContent: 'flex-start' }}
          >
            <FiClipboard size={12} />{' '}
            {getTranslationText('shareableFeature.cards.yourRoutine')}
          </div>
          <div className='hs-routine-body'>
            <div className='hs-routine-row'>
              {steps.map((s, i) => (
                <div
                  key={s.label}
                  style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}
                >
                  <div
                    className={`hs-routine-step ${s.active ? 'active' : ''}`}
                  >
                    <div className='hs-routine-num'>{i + 1}</div>
                    <div className='hs-routine-circle'>{s.icon}</div>
                    <div className='hs-routine-label'>{s.label}</div>
                    <div className='hs-routine-sub'>{s.sub}</div>
                    {s.active && (
                      <div className='hs-add-match-pill'>
                        {getTranslationText(
                          'shareableFeature.cards.addYourMatch',
                        )}
                      </div>
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <div className='hs-routine-arrow'>→</div>
                  )}
                </div>
              ))}
            </div>
            <div className='hs-eco-tip'>
              <div className='hs-eco-tip-title'>
                <FiSun size={12} />{' '}
                {getTranslationText('shareableFeature.cards.ecoTip')}
              </div>
              <div className='hs-eco-tip-text'>{ecoTip}</div>
            </div>
          </div>
        </div>

        <CardFooter />
      </div>
    </div>
  )
}
export const RoutineCard = forwardRef(RoutineCardImpl)

// ──────────────────────────────────────────────────────────────────────────
// Sample data — swap for your real Redux-derived props
// ──────────────────────────────────────────────────────────────────────────
export const SAMPLE_SHARED = {
  photoUrl: null,
  hairTypeCode: '2B',
  hairTypeName: 'WAVY',
  hairGoal: 'Shine + Volume',
  tagline: 'Soft waves. Big volume potential.',
  product: {
    name: 'Trio Mango Bien-être',
    brand: 'Mango Trio',
    description: 'Lightweight nourishment for dull, natural waves.',
    ingredients: ['Mango Seed Oil'],
    image: null,
  },
}

export const SAMPLE_DIAGNOSTICS = {
  ...SAMPLE_SHARED,
  hairMood: {
    title: 'Dull hair',
    desc: 'Feeling a little flat and lacking shine.',
  },
  scalpStory: { title: 'Calm & balanced', desc: 'No current scalp concerns.' },
  insightTitle: 'Dullness + Low Volume',
  insightDesc:
    'Your waves need lightweight hydration to bring back shine and airy volume.',
}

export const SAMPLE_ROUTINE = {
  ...SAMPLE_SHARED,
  ecoTip: 'Keep what already works. Add only what your hair needs.',
  steps: [
    { icon: <FiDroplet size={18} />, label: 'CLEANSE', sub: 'Gentle cleanse' },
    {
      icon: <FiDroplet size={18} />,
      label: 'CONDITION',
      sub: 'Hydrate & soften',
    },
    {
      icon: <FiBarChart2 size={18} />,
      label: 'TREAT',
      sub: 'Nourish & restore',
      active: true,
    },
    { icon: <FiSun size={18} />, label: 'STYLE', sub: 'Keep it light' },
    {
      icon: <FiClipboard size={18} />,
      label: 'CHECK IN',
      sub: 'Review & adjust',
    },
  ],
}
